import { useCallback, useEffect, useState } from "react";
import { ApiError, UNAUTHENTICATED, endpoints } from "./api.js";
import { Routes } from "./routes.jsx";
import { Login } from "./screens/Login.js";

type Auth = "checking" | "in" | "out";

/**
 * The gate.
 *
 * Every `/api` route except login requires a session (apps/api/src/guard.ts), so the app either
 * has one or it is the login screen — there is no third state and no partly-usable shell. A 401
 * from anywhere at any time drops straight back here, because a session that expired mid-sitting
 * should say so rather than rendering an empty library.
 */
export function App() {
  const [auth, setAuth] = useState<Auth>("checking");

  useEffect(() => {
    endpoints
      .me()
      .then(() => setAuth("in"))
      .catch(() => setAuth("out"));
  }, []);

  // Broadcast by the api layer on any 401, so an expired session is caught wherever it surfaces.
  useEffect(() => {
    const onExpired = () => setAuth("out");
    globalThis.addEventListener(UNAUTHENTICATED, onExpired);
    return () => globalThis.removeEventListener(UNAUTHENTICATED, onExpired);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    try {
      await endpoints.login(username, password);
    } catch (cause) {
      // The login screen tells 401 from 429 from a refused origin, so both the status and the code
      // have to survive the throw. Status 0 is the api layer's marker for a request that got no
      // answer at all, and the screen is the only place that reads it as one.
      throw Object.assign(cause instanceof Error ? cause : new Error("login failed"), {
        status: cause instanceof ApiError ? cause.status : 0,
        code: cause instanceof ApiError ? cause.code : "offline",
      });
    }
    setAuth("in");
  }, []);

  // Deliberately blank rather than a spinner: this resolves in one request against the same
  // origin, and a flash of "LOADING" on every page load would be worse than a beat of nothing.
  if (auth === "checking") return null;

  return auth === "in" ? <Routes /> : <Login onLogin={login} />;
}
